import { enableAll } from "./enableAll";

// tslint:disable-next-line:no-namespace
declare global {
    interface JQueryStatic {
        whenAll(array);
        wait(duration);
    }

    interface JQuery {
        /**
         * Search for the jquery.contextmenu.js file to see usage details.
         */
        contextMenu: any;
        /**
         * Attach an event handler function for one or more events to the selected elements, when those elements are not disabled.
         *
         * @param events One or more space-separated event types and optional namespaces, such as "click" or "keydown.myPlugin".
         * @param handler A function to execute when the event is triggered. The value false is also allowed as a shorthand for a function that simply does return false. Rest parameter args is for optional parameters passed to jQuery.trigger(). Note that the actual parameters on the event handler function must be marked as optional (? syntax).
         * @see {@link https://api.jquery.com/on/#on-events-selector-data-handler}
         */
        tie(events: string, handler: (eventObject: JQueryEventObject, ...args: any[]) => any): JQuery;
        /**
         * Attach an event handler function for one or more events to the selected elements, when those elements are not disabled.
         *
         * @param events One or more space-separated event types and optional namespaces, such as "click" or "keydown.myPlugin".
         * @param data Data to be passed to the handler in event.data when an event is triggered.
         * @param handler A function to execute when the event is triggered. The value false is also allowed as a shorthand for a function that simply does return false.
         * @see {@link https://api.jquery.com/on/#on-events-selector-data-handler}
         */
        tie(events: string, data: any, handler: (eventObject: JQueryEventObject, ...args: any[]) => any): JQuery;
        /**
         * Attach an event handler function for one or more events to the selected elements, when those elements are not disabled.
         *
         * @param events One or more space-separated event types and optional namespaces, such as "click" or "keydown.myPlugin".
         * @param selector A selector string to filter the descendants of the selected elements that trigger the event. If the selector is null or omitted, the event is always triggered when it reaches the selected element.
         * @param handler A function to execute when the event is triggered. The value false is also allowed as a shorthand for a function that simply does return false.
         * @see {@link https://api.jquery.com/on/#on-events-selector-data-handler}
         */
        tie(events: string, selector: string, handler: (eventObject: JQueryEventObject, ...eventData: any[]) => any): JQuery;
        /**
         * Attach an event handler function for one or more events to the selected elements, when those elements are not disabled.
         *
         * @param events One or more space-separated event types and optional namespaces, such as "click" or "keydown.myPlugin".
         * @param selector A selector string to filter the descendants of the selected elements that trigger the event. If the selector is null or omitted, the event is always triggered when it reaches the selected element.
         * @param data Data to be passed to the handler in event.data when an event is triggered.
         * @param handler A function to execute when the event is triggered. The value false is also allowed as a shorthand for a function that simply does return false.
         * @see {@link https://api.jquery.com/on/#on-events-selector-data-handler}
         */
        tie(events: string, selector: string, data: any, handler: (eventObject: JQueryEventObject, ...eventData: any[]) => any): JQuery;
        /**
         * Attach an event handler function for one or more events to the selected elements, when those elements are not disabled.
         *
         * @param events An object in which the string keys represent one or more space-separated event types and optional namespaces, and the values represent a handler function to be called for the event(s).
         * @param selector A selector string to filter the descendants of the selected elements that will call the handler. If the selector is null or omitted, the handler is always called when it reaches the selected element.
         * @param data Data to be passed to the handler in event.data when an event occurs.
         * @see {@link https://api.jquery.com/on/#on-events-selector-data}
         */
        tie(events: { [key: string]: any; }, selector?: string, data?: any): JQuery;
        /**
         * Attach an event handler function for one or more events to the selected elements, when those elements are not disabled.
         *
         * @param events An object in which the string keys represent one or more space-separated event types and optional namespaces, and the values represent a handler function to be called for the event(s).
         * @param data Data to be passed to the handler in event.data when an event occurs.
         * @see {@link https://api.jquery.com/on/#on-events-selector-data}
         */
        tie(events: { [key: string]: any; }, data?: any): JQuery;
        setEnabled(isEnabled: boolean): JQuery;
        enable(disabledAttr?: boolean): JQuery;
        disable(disabledAttr?: boolean): JQuery;
        enableAll(includeMyself?: boolean, disabledAttr?: boolean): JQuery;
        disableAll(includeMyself?: boolean, disabledAttr?: boolean): JQuery;
        readOnly(readOnly?: boolean): JQuery;
        readOnlyAll(readOnly: boolean, includeMyself?: boolean): JQuery;
        getPath(): string;
        visible(settings?: JQueryVisibleSettings): boolean;
        hasScrollBar(): boolean;
        mask(args?: any): JQuery;
        unmask(): JQuery;
        getLabelFor(): string;
        zValidator(options?: any): boolean;
        refreshGrid(): JQuery;
        maskGrid(message?: string): JQuery;
        unmaskGrid(): JQuery;
    }

    interface JQueryVisibleSettings {
        partial?: boolean;
        hidden?: boolean;
        direction?: string;
        container?: any;
    }
}

/******jQuery Object Extensions********/
$.fn.extend({
    tie( /*events, selector, data, disableable, handler*/) {
        var handler = null;
        var handlerIndex = null;

        $.each(arguments, function (index, arg) {
            if (Object.prototype.toString.call(arg) == "[object Function]") {
                handler = arg;
                handlerIndex = index;
                return false;
            }
        });

        if (handler && handlerIndex) {
            arguments[handlerIndex] = function (ev) {
                if ($(this).hasClass("disabled") || $(this).hasClass("field-visibility-disabled")) {
                    return;
                }

                handler.call(this, ev);
            };
        }

        return this.on.apply(this, arguments);
    },
    setEnabled(isEnabled) {
        enableAll(this, isEnabled, true);
        return this;
    },
    enable(disabledAttr) {
        enableAll(this, true, disabledAttr);
        return this;
    },
    disable(disabledAttr) {
        enableAll(this, false, disabledAttr);
        return this;
    },
    enableAll(includeMyself, disabledAttr) {
        enableAll(this.find("*"), true, disabledAttr);
        if (includeMyself) {
            enableAll(this, true, disabledAttr);
            this.removeClass("dimmed");
        }
        return this;
    },
    disableAll(includeMyself, disabledAttr) {
        enableAll(this.find("*"), false, disabledAttr);
        if (includeMyself) {
            enableAll(this, false, disabledAttr);
            this.addClass("dimmed");
        }
        return this;
    },
    readOnly(readOnly) {
        readOnlyAll(this, readOnly);
        return this;
    },
    readOnlyAll(readOnly, includeMyself) {
        readOnlyAll(this.find("*"), readOnly);
        if (includeMyself) {
            readOnlyAll(this, readOnly);
        }
        return this;
    },
    getPath() {
        if (this.length != 1) {
            throw new Error("Requires one element.");
        }

        var path;
        var node = this;
        while (node.length) {
            var realNode = node[0];
            var name = realNode.localName;
            if (!name) {
                break;
            }
            name = name.toLowerCase();

            var parent = node.parent();

            var siblings = parent.children(name);
            if (siblings.length > 1) {
                name += ":eq(" + siblings.index(realNode) + ")";
            }

            path = name + (path ? ">" + path : "");
            node = parent;
        }

        return path;
    },
    visible(settings) {
        settings = settings || { direction: "both" };

        if (this.length < 1) {
            return;
        }

        let $w = $(window),
            $t = this.length > 1 ? this.eq(0) : this,
            isContained = settings.container !== undefined && settings.container !== null,
            $c = isContained ? $(settings.container) : $w,
            wPosition = isContained ? $c.position() : 0 as any,
            t = $t.get(0),
            vpWidth = $c.outerWidth(),
            vpHeight = $c.outerHeight(),
            clientSize = settings.hidden === true ? t.offsetWidth * t.offsetHeight : true,
            viewTop = isContained ? 0 : wPosition,
            viewBottom = viewTop + vpHeight,
            viewLeft = $c.scrollLeft(),
            viewRight = viewLeft + vpWidth,
            position = $t.position(),
            top = position.top,
            bottom = top + $t.height(),
            left = position.left,
            right = left + $t.width(),
            compareTop = settings.partial === true ? bottom : top,
            compareBottom = settings.partial === true ? top : bottom,
            compareLeft = settings.partial === true ? right : left,
            compareRight = settings.partial === true ? left : right;

        if (settings.direction === "both") {
            return !!clientSize && ((compareBottom <= viewBottom) && (compareTop >= viewTop)) && ((compareRight <= viewRight) && (compareLeft >= viewLeft));
        } else if (settings.direction === "vertical") {
            return !!clientSize && ((compareBottom <= viewBottom) && (compareTop >= viewTop));
        } else if (settings.direction === "horizontal") {
            return !!clientSize && ((compareRight <= viewRight) && (compareLeft >= viewLeft));
        }
    }
});

$.fn.hasScrollBar = function () {
    return this.get(0).scrollHeight > this.height();
};

$.whenAll = function (array) {
    var slice = [].slice;
    var resolveValues = arguments.length == 1 && $.isArray(array)
        ? array
        : slice.call(arguments);
    var length = resolveValues.length;
    var remaining = length;
    var deferred = $.Deferred();
    var i = 0;
    var failed = 0;
    var rejectContexts = Array(length);
    var rejectValues = Array(length);
    var resolveContexts = Array(length);
    var value;

    function updateFunc(index, contexts, values) {
        return function () {
            !(values === resolveValues) && failed++;
            deferred.notifyWith(
                contexts[index] = this
                , values[index] = slice.call(arguments)
            );
            if (!(--remaining)) {
                deferred[(!failed ? "resolve" : "reject") + "With"](contexts, values);
            }
        };
    }

    for (; i < length; i++) {
        // tslint:disable-next-line:no-conditional-assignment
        if ((value = resolveValues[i]) && $.isFunction(value.promise)) {
            value.promise()
                .done(updateFunc(i, resolveContexts, resolveValues))
                .fail(updateFunc(i, rejectContexts, rejectValues))
            ;
        } else {
            deferred.notifyWith(this, value);
            --remaining;
        }
    }

    if (!remaining) {
        deferred.resolveWith(resolveContexts, resolveValues);
    }

    return deferred.promise();
};

// Pretty Print the setTimeout function
// To call this function do the following:
// $.wait(1000).then(doSomething());
$.wait = function (duration) {
    return $.Deferred(function (def) {
        setTimeout(def.resolve, duration);
    });
};

// Enables or disables every item in the selector
var readOnlyAll = function (selector, readOnly) {
    $.each($(selector), function (i, elem) {
        var element = $(elem);
        var parent = null;
        var data = element.data();
        var dataNames = Object.keys(data);

        if (element.hasClass("field-visibility-disabled")) {
            return;
        }

        $.each(dataNames, function (j, name) {
            if (name.substring(0, 5) == "kendo") {
                parent = element.closest(".k-widget");
                element.data(name).readonly(readOnly);
            }
        });

        if (readOnly) {
            element.siblings("a.js-icon-type").hide();
            element.attr("readonly", "readonly");
            element.addClass("read-only");

            if (parent) {
                parent.attr("readonly", "readonly");
                parent.addClass("read-only");
            }

        } else {
            element.siblings("a.js-icon-type").show();
            element.removeAttr("readonly");
            element.removeClass("read-only");

            if (parent) {
                parent.removeAttr("readonly");
                parent.removeClass("read-only");
            }
        }
    });
};

var oldGrep = $.grep;
var oldEach = $.each;

$.grep = function (array, func, invert) {
    array = array || [];

    return oldGrep.apply($, [array, func, invert]);
};

$.each = function (array, func) {
    array = array || [];

    return oldEach.apply($, [array, func]);
};
